import { recordingSentences } from '../data'

export type ChildProfile = { name: string; age: string; language: string }
export type AppPreferences = { theme: 'system' | 'light' | 'dark'; subtitles: boolean; sleepTimer: '15' | '30' | 'end' }
export type PlaybackState = { storyId: string; generationId?: string; position: number; updatedAt: string }
export type ServerConsentState = { id: string; agreedAt: string; policyVersion: string; scopes: string[] }
export type ServerVoiceProfileState = { id: string; status: string; provider: string; createdAt: string; name?: string; approvalStatus?: string; lifecycle?: string; previewStatus?: string; previewChecksum?: string; conversionReferenceStatus?: string }
export type ServerGenerationState = {
  id?: string
  idempotencyKey: string
  status: 'creating' | 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled' | 'deleted' | 'ready' | 'reviewing'
  progress: number
  outputUrl?: string
  error?: string
  updatedAt: string
  voiceProfileId?: string
  voiceName?: string
  contentVersionId?: string
  phase?: string
  completedSegments?: number
  totalSegments?: number
  assetState?: string
  expiresAt?: string
  checksumSha256?: string
  durationSeconds?: number
  canRetry?: boolean
}
export type LocalState = {
  version: 1 | 2
  ownerId?: string | null
  onboarded: boolean
  profile: ChildProfile | null
  consentAt: string | null
  voiceReady: boolean
  voiceLabel: string
  library: string[]
  playback: PlaybackState | null
  preferences: AppPreferences
  serverConsent: ServerConsentState | null
  serverRecordingIds: string[]
  serverRecordingFingerprint?: string | null
  serverVoiceProfile: ServerVoiceProfileState | null
  serverVoiceCandidate?: ServerVoiceProfileState | null
  serverGenerations: Record<string, ServerGenerationState>
}

const STATE_KEY = 'lulla.local-state.v1'
const OWNER_KEY = 'lulla.local-owner.v2'
let currentOwner = localStorage.getItem(OWNER_KEY)
const stateKey = () => `lulla.local-state.v2:${currentOwner ?? 'guest'}`
export const defaultState: LocalState = {
  version: 2, ownerId: null, onboarded: false, profile: null, consentAt: null, voiceReady: false, voiceLabel: '엄마',
  library: [], playback: null,
  preferences: { theme: 'system', subtitles: false, sleepTimer: '15' },
  serverConsent: null, serverRecordingIds: [], serverRecordingFingerprint: null, serverVoiceProfile: null, serverGenerations: {}
}

export const LocalProfileRepository = {
  owner() { return currentOwner },
  setOwner(ownerId: string | null) { currentOwner = ownerId; if (ownerId) localStorage.setItem(OWNER_KEY, ownerId); else localStorage.removeItem(OWNER_KEY) },
  load(): LocalState {
    try {
      const value = localStorage.getItem(stateKey())
      if (!value) return { ...defaultState, ownerId: currentOwner }
      const saved = JSON.parse(value) as Partial<LocalState>
      return { ...defaultState, ...saved, ownerId: currentOwner, version: 2, preferences: { ...defaultState.preferences, ...saved.preferences } }
    } catch { return { ...defaultState, ownerId: currentOwner } }
  },
  save(state: LocalState) { if ((state.ownerId ?? null) !== currentOwner) return; localStorage.setItem(stateKey(), JSON.stringify({ ...state, version: 2 })) },
  clear() { localStorage.removeItem(stateKey()) },
  legacy() { try { return JSON.parse(localStorage.getItem(STATE_KEY) || 'null') as LocalState | null } catch { return null } },
  importLegacy() { const legacy = this.legacy(); if (!legacy) return; this.save({ ...legacy, ownerId: currentOwner, version: 2, serverConsent: null, serverRecordingIds: [], serverRecordingFingerprint: null, serverVoiceProfile: null, serverGenerations: {} }); localStorage.removeItem(STATE_KEY) }
}

export const LocalPlaybackRepository = {
  save(playback: PlaybackState | null) {
    const state = LocalProfileRepository.load()
    LocalProfileRepository.save({ ...state, playback })
  },
  load() { return LocalProfileRepository.load().playback }
}

const DB_NAME = 'lulla-audio-v1'
const VOICE_STORE = 'voice-segments'
const GENERATED_STORE = 'generated-audio'
const DB_VERSION = 3
const openDb = () => new Promise<IDBDatabase>((resolve, reject) => {
  const request = indexedDB.open(DB_NAME, DB_VERSION)
  request.onupgradeneeded = () => {
    if (!request.result.objectStoreNames.contains(VOICE_STORE)) request.result.createObjectStore(VOICE_STORE)
    if (!request.result.objectStoreNames.contains(GENERATED_STORE)) request.result.createObjectStore(GENERATED_STORE)
  }
  request.onsuccess = () => resolve(request.result)
  request.onerror = () => reject(request.error)
})

const transact = async <T>(storeName: string, mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>) => {
  const db = await openDb()
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(storeName, mode)
    let request: IDBRequest<T>
    try { request = run(tx.objectStore(storeName)) } catch (reason) { db.close(); reject(reason); return }
    let result: T
    request.onsuccess = () => { result = request.result }
    request.onerror = () => reject(request.error)
    tx.oncomplete = () => { db.close(); resolve(result) }
    tx.onerror = () => { db.close(); reject(tx.error) }
    tx.onabort = () => { db.close(); reject(tx.error ?? new Error('IndexedDB transaction aborted')) }
  })
}

export const LocalVoiceRepository = {
  put(index: number, blob: Blob) {
    const owner = currentOwner
    return transact(VOICE_STORE, 'readwrite', store => {
      if (owner !== currentOwner) throw new Error('계정이 변경되어 녹음 저장을 중단했어요.')
      return store.put(blob, `${owner ?? 'guest'}:segment-${index}`)
    })
  },
  get(index: number) { return transact<Blob | undefined>(VOICE_STORE, 'readonly', store => store.get(`${currentOwner ?? 'guest'}:segment-${index}`)) },
  remove(index: number) { return transact(VOICE_STORE, 'readwrite', store => store.delete(`${currentOwner ?? 'guest'}:segment-${index}`)) },
  clear() { return Promise.all(recordingSentences.map((_, index) => this.remove(index))) },
  async status() { return Promise.all(recordingSentences.map(async (_, index) => Boolean(await this.get(index)))) }
}

export type LocalLongReference = {
  ownerId: string
  profileId: string
  consentAt: string
  savedAt: string
  durationSeconds: number
  voicedSeconds: number
  rms: number
  peak: number
  checksumSha256: string
  serverConsentId?: string
  blob: Blob
}

const longReferenceKey = (owner: string) => `${owner}:long-reference-v1`
export const LocalLongReferenceRepository = {
  async put(record: Omit<LocalLongReference, 'ownerId' | 'checksumSha256'>) {
    const owner = currentOwner
    if (!owner || !record.profileId || owner !== currentOwner) throw new Error('보호자 계정으로 로그인한 뒤 다시 준비해 주세요.')
    const value: LocalLongReference = { ...record, ownerId: owner, checksumSha256: await audioChecksum(record.blob) }
    if (owner !== currentOwner) throw new Error('계정이 변경되어 저장을 중단했어요.')
    await transact(VOICE_STORE, 'readwrite', store => store.put(value, longReferenceKey(owner)))
    const saved = await this.get()
    if (!saved || saved.checksumSha256 !== value.checksumSha256) throw new Error('장문 녹음 저장을 확인하지 못했어요.')
  },
  async get() {
    const owner = currentOwner
    if (!owner) return undefined
    const value = await transact<LocalLongReference | undefined>(VOICE_STORE, 'readonly', store => store.get(longReferenceKey(owner)))
    if (owner !== currentOwner) return undefined
    if (value?.blob && await audioChecksum(value.blob) !== value.checksumSha256) throw new Error('저장된 장문 녹음 파일을 확인하지 못했어요.')
    return value
  },
  async updateServerConsent(profileId: string, checksumSha256: string, expectedId: string | null, nextId: string | null) {
    const owner = currentOwner
    if (!owner) throw new Error('보호자 계정을 다시 확인해 주세요.')
    const db = await openDb()
    return new Promise<LocalLongReference>((resolve, reject) => {
      const tx = db.transaction(VOICE_STORE, 'readwrite')
      const store = tx.objectStore(VOICE_STORE)
      let updated: LocalLongReference | undefined
      let failure: Error | undefined
      const request = store.get(longReferenceKey(owner))
      request.onsuccess = () => {
        const saved = request.result as LocalLongReference | undefined
        if (owner !== currentOwner || !saved || saved.ownerId !== owner || saved.profileId !== profileId || saved.checksumSha256 !== checksumSha256) {
          failure = new Error('기기의 장문 원본이 변경됐어요. 현재 파일을 다시 확인해 주세요.')
          tx.abort()
          return
        }
        if ((saved.serverConsentId ?? null) !== expectedId) {
          failure = new Error('서버 전송 동의가 다른 창에서 변경됐어요. 화면을 다시 열어 주세요.')
          tx.abort()
          return
        }
        updated = { ...saved, serverConsentId: nextId ?? undefined }
        store.put(updated, longReferenceKey(owner))
      }
      tx.oncomplete = () => { db.close(); resolve(updated!) }
      tx.onerror = () => { db.close(); reject(failure ?? tx.error ?? new Error('장문 녹음 정보를 수정하지 못했어요.')) }
      tx.onabort = () => { db.close(); reject(failure ?? tx.error ?? new Error('장문 녹음 정보를 수정하지 못했어요.')) }
    })
  },
  clear() {
    const owner = currentOwner
    if (!owner) return Promise.resolve()
    return this.clearOwner(owner)
  },
  clearOwner(owner: string) { return transact(VOICE_STORE, 'readwrite', store => store.delete(longReferenceKey(owner))) },
}

export type GeneratedAudioRecord = {
  ownerId?: string
  contentVersionId?: string
  voiceName?: string
  checksumSha256?: string
  sizeBytes?: number
  durationSeconds?: number
  experimentalPreview?: boolean
  modelRevision?: string
  pipelineVersion?: string
  qualityStatus?: string
  qualityWarningCount?: number
  storyId: string
  voiceProfileId: string
  generationId: string
  title: string
  contentType: string
  savedAt: string
  blob: Blob
}

const generatedKey = (record: GeneratedAudioRecord) => `${record.ownerId}:${record.generationId}:${record.contentVersionId ?? 'legacy'}:${record.voiceProfileId}`
export const audioChecksum = async (blob: Blob) => Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', await blob.arrayBuffer())), value => value.toString(16).padStart(2, '0')).join('')

export const LocalGeneratedAudioRepository = {
  async put(record: GeneratedAudioRecord) {
    const owner = currentOwner
    if (!owner || (record.ownerId && owner !== record.ownerId)) throw new Error('로그인한 계정의 저장 공간을 확인해 주세요.')
    const checksum = await audioChecksum(record.blob)
    if (record.checksumSha256 && record.checksumSha256 !== checksum) throw new Error('음원 검증에 실패했어요. 다시 받아 주세요.')
    const value = { ...record, ownerId: owner, checksumSha256: checksum, sizeBytes: record.blob.size }
    if (owner !== currentOwner) throw new Error('계정이 변경되어 저장을 중단했어요.')
    await transact(GENERATED_STORE, 'readwrite', store => store.put(value, generatedKey(value)))
    const saved = await transact<GeneratedAudioRecord>(GENERATED_STORE, 'readonly', store => store.get(generatedKey(value)))
    if (!saved?.blob || await audioChecksum(saved.blob) !== checksum) throw new Error('저장 확인에 실패했어요.')
  },
  async get(storyId: string, voiceProfileId?: string) {
    const records = await this.list(); return records.filter(record => record.storyId === storyId && (!voiceProfileId || record.voiceProfileId === voiceProfileId)).sort((a, b) => b.savedAt.localeCompare(a.savedAt))[0]
  },
  async byJob(jobId: string) { return (await this.list()).find(record => record.generationId === jobId) },
  async remove(storyId: string, voiceProfileId?: string) {
    const records = (await this.list()).filter(record => record.storyId === storyId && (!voiceProfileId || record.voiceProfileId === voiceProfileId)); await Promise.all(records.map(record => this.removeJob(record.generationId)))
  },
  removeJob(jobId: string) { const owner = currentOwner; return this.list().then(records => { if (owner !== currentOwner) throw new Error('계정이 변경되었어요.'); const record = records.find(item => item.generationId === jobId); if (record) return transact(GENERATED_STORE, 'readwrite', store => store.delete(generatedKey(record))) }) },
  async list() {
    const owner = currentOwner; const records = await transact<GeneratedAudioRecord[]>(GENERATED_STORE, 'readonly', store => store.getAll()); return owner && currentOwner === owner ? records.filter(record => record.ownerId === owner) : []
  },
  async legacy() { return (await transact<GeneratedAudioRecord[]>(GENERATED_STORE, 'readonly', store => store.getAll())).filter(record => !record.ownerId) },
  async importLegacy(record: GeneratedAudioRecord) { await this.put(record); await transact(GENERATED_STORE, 'readwrite', store => store.delete(`${record.storyId}:${record.voiceProfileId}`)) },
  async removeLegacy(record: GeneratedAudioRecord) { await transact(GENERATED_STORE, 'readwrite', store => store.delete(`${record.storyId}:${record.voiceProfileId}`)) },
  async clear() { const records = await this.list(); await Promise.all(records.map(record => this.removeJob(record.generationId))) },
}

export const clearAllLocalData = async () => {
  await Promise.all([LocalVoiceRepository.clear(), LocalLongReferenceRepository.clear(), LocalGeneratedAudioRepository.clear()])
  LocalProfileRepository.clear()
}

